import { useEffect, useState, useCallback } from "react";
import { getDiscoverySources, getDiscoveredSources, setSourceStatus, setSourceTier, getDiscoveryMix } from "@/lib/apiClient";
import AdminNav from "@/components/AdminNav";
import { toast } from "sonner";

const TIER_LABEL = {
  0: "Open Web", 1: "Entrepreneurial", 2: "Vertical AI", 3: "Agency",
  4: "AI Product", 5: "Company Direct", 6: "Publication", 7: "Big Tech",
};
const TIER_OPTIONS = [1, 2, 3, 4, 5, 6];
const statusStyle = {
  ACTIVE: "bg-brlime text-ink", TESTING: "bg-bryellow text-ink",
  "LOW VALUE": "bg-secondary text-ink/60", PAUSED: "bg-secondary text-ink/60", IGNORED: "bg-brorange text-white",
};

function Bar({ data }) {
  const total = Object.values(data || {}).reduce((a, b) => a + b, 0) || 1;
  const colors = ["bg-cobalt", "bg-brorange", "bg-brlime", "bg-bryellow", "bg-brpink", "bg-ink"];
  return (
    <div className="space-y-1.5">
      {Object.entries(data || {}).map(([k, v], i) => (
        <div key={k} className="flex items-center gap-2 text-sm">
          <span className="w-40 shrink-0 text-ink/70 truncate">{k}</span>
          <div className="flex-1 bg-secondary h-4 border border-ink/10"><div className={`h-full ${colors[i % colors.length]}`} style={{ width: `${Math.round(100 * v / total)}%` }} /></div>
          <span className="w-16 text-right text-ink/60">{v} · {Math.round(100 * v / total)}%</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminSources() {
  const [sources, setSources] = useState([]);
  const [discovered, setDiscovered] = useState([]);
  const [mix, setMix] = useState(null);
  const [tierSel, setTierSel] = useState({});

  const load = useCallback(() => {
    getDiscoverySources().then(setSources);
    getDiscoveredSources().then(setDiscovered);
    getDiscoveryMix().then(setMix);
  }, []);
  useEffect(load, [load]);

  const act = async (name, status) => {
    await setSourceStatus(name, status);
    toast.success(`${name}: ${status}`);
    load();
  };

  const addToRegistry = async (name, tier) => {
    await setSourceTier(name, tier, "ACTIVE");
    toast.success(`${name} added as ${TIER_LABEL[tier]} (Tier ${tier})`);
    load();
  };

  const registered = sources.filter((s) => !s.discovered || s.status === "ACTIVE");

  return (
    <div>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="eyebrow text-ink/60">Where the stories live</p>
        <h1 className="font-display font-extrabold text-4xl display-tight uppercase">Sources</h1>
        <p className="text-sm text-ink/60 mt-1">AI YAI YAI discovers both the stories and the places they live. New libraries are tested before they count.</p>

        {/* Discovery mix */}
        {mix && (
          <div className="mt-6 grid md:grid-cols-3 gap-5">
            <div className="border-2 border-ink p-4"><p className="eyebrow text-ink/50 mb-3">By source type</p><Bar data={mix.by_source_category} /></div>
            <div className="border-2 border-ink p-4"><p className="eyebrow text-ink/50 mb-3">By business size</p><Bar data={mix.by_business_size} /></div>
            <div className="border-2 border-ink p-4"><p className="eyebrow text-ink/50 mb-3">By region</p><Bar data={mix.by_region} /></div>
          </div>
        )}
        {mix?.alerts?.length > 0 && (
          <div className="mt-4 border-2 border-brorange bg-brorange/10 p-4" data-testid="bias-alerts">
            <p className="eyebrow text-brorange mb-1">Discovery balance</p>
            {mix.alerts.map((a, i) => <p key={i} className="text-sm text-ink/80">⚠ {a}</p>)}
          </div>
        )}

        {/* New sources found */}
        <h2 className="font-display font-extrabold text-2xl uppercase mt-10">New Sources Found</h2>
        <p className="text-sm text-ink/50">Discovered case-study libraries awaiting your decision.</p>
        <div className="mt-3 space-y-2" data-testid="discovered-sources">
          {discovered.filter((s) => s.status === "TESTING").length === 0 ? (
            <div className="border-2 border-ink p-6 text-center text-ink/40 text-sm">No new sources awaiting review. Run discovery to find more.</div>
          ) : discovered.filter((s) => s.status === "TESTING").map((s) => (
            <div key={s.name} className="border-2 border-ink p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`discovered-${s.name}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="eyebrow bg-ink text-white px-2 py-0.5">{TIER_LABEL[s.tier] || "?"}</span>
                  <span className="font-bold truncate">{s.title || s.name}</span>
                </div>
                <a href={s.library_url.startsWith("http") ? s.library_url : `https://${s.library_url}`} target="_blank" rel="noreferrer" className="text-xs text-cobalt truncate block">{s.library_url}</a>
                <div className="text-xs text-ink/40 mt-0.5">Found via: {s.discovered_via}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => addToRegistry(s.name, 1)} data-testid={`source-promote-t1-${s.name}`} title="Promote straight to Tier 1 (entrepreneurial / SME library)" className="btn-tactile bg-brlime text-ink px-3 h-9 text-sm font-bold border-2 border-ink hover:bg-cobalt hover:text-white">→ Tier 1</button>
                <select value={tierSel[s.name] ?? s.tier ?? 4} onChange={(e) => setTierSel((m) => ({ ...m, [s.name]: Number(e.target.value) }))} data-testid={`source-tier-select-${s.name}`} className="h-9 border-2 border-ink bg-white px-2 text-sm font-semibold">
                  {TIER_OPTIONS.map((t) => <option key={t} value={t}>Tier {t} · {TIER_LABEL[t]}</option>)}
                </select>
                <button onClick={() => addToRegistry(s.name, tierSel[s.name] ?? s.tier ?? 4)} data-testid={`source-add-${s.name}`} className="btn-tactile bg-ink text-white px-3 h-9 text-sm font-bold hover:bg-cobalt">Add</button>
                <button onClick={() => act(s.name, "IGNORED")} data-testid={`source-ignore-${s.name}`} className="btn-tactile border-2 border-ink px-3 h-9 text-sm font-semibold hover:bg-brorange hover:text-white">Ignore</button>
              </div>
            </div>
          ))}
        </div>

        {/* Registry */}
        <h2 className="font-display font-extrabold text-2xl uppercase mt-10">Source Registry</h2>
        <div className="mt-3 border-2 border-ink overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]" data-testid="source-registry-table">
            <thead><tr className="bg-ink text-white text-left">
              <th className="px-3 py-3">Source</th><th className="px-3 py-3">Tier</th><th className="px-3 py-3">Category</th>
              <th className="px-3 py-3">Pages</th><th className="px-3 py-3">Qualified</th><th className="px-3 py-3">Rate</th>
              <th className="px-3 py-3">Last</th><th className="px-3 py-3">Status</th>
            </tr></thead>
            <tbody>
              {registered.sort((a, b) => (b.candidates || 0) - (a.candidates || 0)).map((s) => {
                const rate = s.pages_checked ? Math.round(100 * (s.qualified || 0) / s.pages_checked) : 0;
                return (
                  <tr key={s.name} className="border-t border-ink/10">
                    <td className="px-3 py-2.5 font-semibold max-w-[200px] truncate">{s.name}</td>
                    <td className="px-3 py-2.5">{TIER_LABEL[s.tier] ?? s.tier}</td>
                    <td className="px-3 py-2.5 text-ink/60">{s.category || "—"}</td>
                    <td className="px-3 py-2.5">{s.pages_checked || 0}</td>
                    <td className="px-3 py-2.5 font-semibold">{s.qualified || 0}</td>
                    <td className="px-3 py-2.5">{rate}%</td>
                    <td className="px-3 py-2.5 text-ink/40">{s.last_checked ? new Date(s.last_checked).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-2.5"><span className={`eyebrow px-2 py-0.5 ${statusStyle[s.status] || ""}`}>{s.status || "ACTIVE"}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
